import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import QuestionnairePage from './pages/QuestionnairePage';
import ComparisonPage from './pages/ComparisonPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminConsole from './pages/AdminConsole';
import RespondentGuidePage from './pages/RespondentGuidePage';
import AdminGuidePage from './pages/AdminGuidePage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import StateOfEsgPage from './pages/StateOfEsgPage';
import TelemetryPage from './pages/TelemetryPage';
import AITransparencyPage from './pages/AITransparencyPage';

// Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Axios setup
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const LOGIN_ENDPOINTS = ['/api/auth/login', '/api/auth/member-login'];

api.interceptors.response.use(
  res => res,
  error => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status === 401 && !LOGIN_ENDPOINTS.some(e => url.includes(e))) {
      localStorage.removeItem('token');
      if (!url.includes('/api/auth/me')) {
        toast.error('Your session has expired or was signed out in another tab. Please sign in again.');
        const isAdminArea = window.location.pathname.startsWith('/admin');
        setTimeout(() => {
          window.location.href = isAdminArea ? '/admin-login' : '/auth';
        }, 1200);
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);

export { api };

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/api/auth/me')
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    setUser(res.data);
    return res.data;
  };

  const loginMember = async (company_code, invite_code) => {
    const res = await api.post('/api/auth/member-login', { company_code, invite_code });
    localStorage.setItem('token', res.data.token);
    setUser(res.data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ user, login, loginMember, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/questionnaire" replace />;
  }

  return children;
}

// Main App
function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/state-of-esg" element={<StateOfEsgPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route 
          path="/questionnaire" 
          element={
            <ProtectedRoute>
              <QuestionnairePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/comparison" 
          element={
            <ProtectedRoute>
              <ComparisonPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/guide" 
          element={
            <ProtectedRoute>
              <RespondentGuidePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/guide" 
          element={
            <ProtectedRoute adminOnly>
              <AdminGuidePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/console" 
          element={
            <ProtectedRoute adminOnly>
              <AdminConsole />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/telemetry" 
          element={
            <ProtectedRoute adminOnly>
              <TelemetryPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/ai-transparency" 
          element={
            <ProtectedRoute adminOnly>
              <AITransparencyPage />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </AuthProvider>
  );
}

export default App;
